import "./App.css";
import { Route, Routes, Navigate } from "react-router-dom";
import React from "react";
import { ThemeProvider } from "@mui/material/styles";
import { getTheme } from "./theme";
import {
  DASHBOARD_ROUTE,
  SIGNIN_ROUTE,
  FORGOTPASSWORD_ROUTE,
  SIGNUP_ROUTE,
  UPDATE_PASSWORD_ROUTE,
  AUTHENTIFICATION_ROUTE,
  REGISTERED_ROUTE,
  VERIFY_EMAIL_ROUTE,
  SSO_CALLBACK_ROUTE,
} from "./constants/RoutePaths";
import Signin from "./components/Signin";
import Layouts from "./components/Layouts";
import Forgotpassword from "./components/Forgotpassword";
import Authentication from "./components/Authentication";
import Signup from "./components/Signup";
import NotFound from "./components/NotFound";
import { PrivateRoute, PublicRoute } from "./common/mainfunction";
import Updatepassword from "./components/Updatepassword";
import Registered from "./components/Registered";
import VerifyEmail from "./components/VerifyEmail";
import ErrorBoundary from "./components/ErrorBoundary";
import SSOCallback from "./components/SSOCallback";
import MobileBlocker from "./components/MobileBlocker";
import { PermissionProvider } from "./context/PermissionContext";

const lightTheme = getTheme(false);

interface ForcedLightWrapperProps {
  children: React.ReactNode;
}

// Always renders children with light theme regardless of user preference.
// Used for auth pages (login, forgot password, etc.) where the user hasn't
// had a chance to set their theme preference yet.
function ForcedLightWrapper({ children }: ForcedLightWrapperProps): React.ReactElement {
  return (
    <ThemeProvider theme={lightTheme}>
      <div data-theme="light" style={{ minHeight: "100vh", backgroundColor: "#f9f9fa" }}>
        {children}
      </div>
    </ThemeProvider>
  );
}

function App(): React.ReactElement {
  const token = localStorage.getItem("AUTH_TOKEN");

  return (
    <div className="App">
      <MobileBlocker>
        <ErrorBoundary>
          <Routes>
            <Route
              index
              path="/"
              element={token ? <Navigate to={DASHBOARD_ROUTE} /> : <Navigate to={SIGNIN_ROUTE} />}
            ></Route>
            <Route
              index
              path="/*"
              element={
                <PrivateRoute>
                  <PermissionProvider>
                    <Layouts />
                  </PermissionProvider>
                </PrivateRoute>
              }
            ></Route>
            <Route
              path={SIGNIN_ROUTE}
              element={
                <ForcedLightWrapper>
                  <PublicRoute>
                    <Signin />
                  </PublicRoute>
                </ForcedLightWrapper>
              }
            ></Route>
            <Route
              path={REGISTERED_ROUTE}
              element={
                <ForcedLightWrapper>
                  <Registered />
                </ForcedLightWrapper>
              }
            ></Route>
            <Route
              path={VERIFY_EMAIL_ROUTE}
              element={
                <ForcedLightWrapper>
                  <VerifyEmail />
                </ForcedLightWrapper>
              }
            ></Route>
            <Route
              path={FORGOTPASSWORD_ROUTE}
              element={
                <ForcedLightWrapper>
                  <PublicRoute>
                    <Forgotpassword />
                  </PublicRoute>
                </ForcedLightWrapper>
              }
            ></Route>
            <Route
              path={AUTHENTIFICATION_ROUTE}
              element={
                <ForcedLightWrapper>
                  <PublicRoute>
                    <Authentication />
                  </PublicRoute>
                </ForcedLightWrapper>
              }
            ></Route>
            <Route
              path={SSO_CALLBACK_ROUTE}
              element={
                <ForcedLightWrapper>
                  <SSOCallback />
                </ForcedLightWrapper>
              }
            ></Route>
            <Route
              path={UPDATE_PASSWORD_ROUTE}
              element={
                <ForcedLightWrapper>
                  <Updatepassword />
                </ForcedLightWrapper>
              }
            ></Route>
            <Route
              path={SIGNUP_ROUTE}
              element={
                <ForcedLightWrapper>
                  <PublicRoute>
                    <Signup />
                  </PublicRoute>
                </ForcedLightWrapper>
              }
            ></Route>
            <Route path="pagenotfound" element={<NotFound />}></Route>
          </Routes>
        </ErrorBoundary>
      </MobileBlocker>
    </div>
  );
}

export default App;
